import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ConnectStepPoints, ConnectSteps } from "../../const";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { setAddKeysStatus } from "../../store/actions/keys";
import {
  loadAmountToStart,
  setChangePositionModeStatus,
  setCheckPositionModeStatus,
  setLoadAmountToStartStatus,
  setSendAmountToStartStatus,
} from "../../store/actions/start";
import BinanceSpotConnectStep1 from "./steps/binance-spot-steps/binance-spot-connect-step1";
import BinanceSpotConnectStep2 from "./steps/binance-spot-steps/binance-spot-connect-step2";
import BinanceSpotConnectStep3 from "./steps/binance-spot-steps/binance-spot-connect-step3";
import { setCurrencyList, setSelectedCurrency } from "../../store/actions";
import { AmountToStartType } from "../../types/details";

type BinanceSpotStepsToConnectProps = {
  activeStep: string;
  setActiveStep: Dispatch<SetStateAction<string>>;
  isStepsAnimationEnd?: boolean;
};

export default function BinanceSpotStepsToConnect({
  activeStep,
  setActiveStep,
  isStepsAnimationEnd,
}: BinanceSpotStepsToConnectProps): JSX.Element {
  const dispatch = useAppDispatch();
  const {
    isChangePositionModeLoading,
    amountToStart,
    addKeysStatus,
    loadAmountToStartStatus,
    changePositionModeStatus,
    sendAmountToStartStatus,
    isSendAmountForStartLoading,
    isAddKeysLoading,
    isLoadAmountToStartLoading,
    checkPositionModeStatus,
    isCheckPositionModeLoading,
    selectedCurrency,
  } = useAppSelector((state) => state);

  const [availableAmount, setAvailableAmount] = useState<number>(0);

  const getHighestAmountCurrency = () => {
    if (!amountToStart) return null;
    return Object.entries(amountToStart).reduce<{
      key: string | null;
      value: number;
    }>((max, [key, value]) => (value > max.value ? { key, value } : max), {
      key: null,
      value: -Infinity,
    });
  };

  useEffect(() => {
    if (!amountToStart) return;
    const highestCurrency = getHighestAmountCurrency();

    if (highestCurrency?.key) {
      dispatch(setSelectedCurrency(highestCurrency.key));
      setAvailableAmount(Math.floor(highestCurrency.value));
    } else {
      const firstEntry = Object.entries(amountToStart)[0];
      if (firstEntry) {
        const [firstKey, firstValue] = firstEntry;
        dispatch(setSelectedCurrency(firstKey));
        setAvailableAmount(Math.floor(firstValue));
      }
    }

    dispatch(setCurrencyList(Object.keys(amountToStart)));
  }, [amountToStart, dispatch]);

  useEffect(() => {
    if (!amountToStart || !selectedCurrency) return;
    const currencyValue =
      amountToStart[selectedCurrency as keyof AmountToStartType];
    if (currencyValue !== undefined) {
      setAvailableAmount(Math.floor(currencyValue));
    }
  }, [selectedCurrency, amountToStart]);

  useEffect(() => {
    return () => {
      dispatch(setAddKeysStatus(null));
      dispatch(loadAmountToStart(null));
      dispatch(setLoadAmountToStartStatus(null));
      dispatch(setCheckPositionModeStatus(null));
      dispatch(setChangePositionModeStatus(null));
      dispatch(setSendAmountToStartStatus(null));
      dispatch(setSelectedCurrency(null));
      dispatch(setCurrencyList(null));
      setActiveStep(ConnectSteps.STEP1);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <BinanceSpotConnectStep1
        pointId={ConnectStepPoints.STEP_POINT1}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        isStepsAnimationEnd={isStepsAnimationEnd}
      />
      <BinanceSpotConnectStep2
        pointId={ConnectStepPoints.STEP_POINT2}
        activeStep={activeStep}
        addKeysStatus={addKeysStatus}
        setActiveStep={setActiveStep}
        isLoading={
          isAddKeysLoading ||
          isLoadAmountToStartLoading ||
          isCheckPositionModeLoading
        }
        loadAmountToStartStatus={loadAmountToStartStatus}
        checkPositionModeStatus={checkPositionModeStatus}
      />
      <BinanceSpotConnectStep3
        pointId={ConnectStepPoints.STEP_POINT3}
        activeStep={activeStep}
        availableAmount={availableAmount}
        isLoading={
          isChangePositionModeLoading ||
          isSendAmountForStartLoading ||
          isLoadAmountToStartLoading ||
          isCheckPositionModeLoading
        }
        setActiveStep={setActiveStep}
        sendAmountToStartStatus={sendAmountToStartStatus}
        changePositionModeStatus={changePositionModeStatus}
        loadAmountToStartStatus={loadAmountToStartStatus}
        checkPositionModeStatus={checkPositionModeStatus}
      />
    </>
  );
}
